import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import Layout from "../components/Layout";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Plus, UploadSimple, Trash, Archive, Pencil, FileText } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function AdmissionLetterTemplatesPage() {
  const [templates, setTemplates] = useState([]);
  const [programmes, setProgrammes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [programmeId, setProgrammeId] = useState("");
  const [description, setDescription] = useState("");
  const [file, setFile] = useState(null);
  const [scope, setScope] = useState("both");
  const [source, setSource] = useState("file"); // 'file' | 'link'
  const [externalUrl, setExternalUrl] = useState("");
  const [saving, setSaving] = useState(false);
  const [includeArchived, setIncludeArchived] = useState(false);

  const load = () => {
    setLoading(true);
    api.get(`/admission-letter-templates?include_archived=${includeArchived}`).then(r => {
      setTemplates(r.data || []); setLoading(false);
    }).catch(() => setLoading(false));
  };
  useEffect(() => { load(); }, [includeArchived]);
  useEffect(() => { api.get("/programmes").then(r => setProgrammes(r.data || [])).catch(() => {}); }, []);

  const submit = async () => {
    if (!name) return toast.error("Name is required");
    if (source === "file" && !file) return toast.error("Pick a file or switch to link");
    if (source === "link" && !externalUrl.trim()) return toast.error("Paste a URL or switch to file");
    setSaving(true);
    const fd = new FormData();
    if (source === "file") fd.append("file", file);
    fd.append("name", name);
    fd.append("programme_id", programmeId);
    fd.append("description", description);
    fd.append("scope", scope);
    if (source === "link") fd.append("external_url", externalUrl.trim());
    try {
      await api.post("/admission-letter-templates", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Template saved");
      setOpen(false); setName(""); setProgrammeId(""); setDescription(""); setFile(null); setScope("both"); setSource("file"); setExternalUrl("");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
    setSaving(false);
  };

  const archive = async (id) => {
    if (!window.confirm("Archive this template?")) return;
    try { await api.delete(`/admission-letter-templates/${id}`); toast.success("Archived"); load(); }
    catch { toast.error("Failed"); }
  };

  const download = (id) => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/admission-letter-templates/${id}/download`, "_blank");

  return (
    <Layout>
      <div className="p-6 md:p-8 lg:p-12" data-testid="admission-templates-page">
        <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-1">Document Library</p>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-950" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Document Templates
            </h1>
            <p className="text-sm text-slate-500 mt-1">Reusable templates for letters, certificates, and other documents.</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setIncludeArchived(v => !v)} className="rounded-none" data-testid="toggle-archived">
              {includeArchived ? "Hide archived" : "Show archived"}
            </Button>
            <Button onClick={() => setOpen(true)} className="bg-blue-800 text-white hover:bg-blue-900 rounded-none gap-2" data-testid="new-template-btn">
              <Plus size={14} weight="bold" /> Upload Template
            </Button>
          </div>
        </div>

        {loading ? <p className="text-sm text-slate-500">Loading…</p> : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {templates.length === 0 && <p className="text-sm text-slate-500 col-span-full">No templates yet. Upload your first template above.</p>}
            {templates.map(t => (
              <Card key={t.id} className="rounded-none border border-slate-200 hover:shadow-md transition-all" data-testid={`template-${t.id}`}>
                <CardContent className="p-5">
                  <div className="flex items-start gap-3 mb-3">
                    <div className="bg-blue-100 text-blue-800 w-10 h-10 flex items-center justify-center"><FileText size={20} weight="fill" /></div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-slate-950 text-sm leading-snug truncate">{t.name}</p>
                      <p className="text-xs text-slate-500 truncate">{t.filename}</p>
                      {t.archived && <span className="inline-block mt-1 px-1.5 py-0.5 bg-slate-200 text-[10px] uppercase tracking-wider text-slate-700">Archived</span>}
                    </div>
                  </div>
                  {t.description && <p className="text-xs text-slate-600 mb-3 line-clamp-3">{t.description}</p>}
                  {t.programme_id && <p className="text-xs text-slate-500 mb-3">Programme: {programmes.find(p => p.id === t.programme_id)?.title || t.programme_id}</p>}
                  <div className="flex gap-2 flex-wrap">
                    {t.kind === "link" || t.external_url ? (
                      <a href={t.external_url} target="_blank" rel="noopener noreferrer">
                        <Button size="sm" variant="outline" className="rounded-none text-xs gap-1" data-testid={`open-template-${t.id}`}><UploadSimple size={12} className="rotate-45" /> Open Link</Button>
                      </a>
                    ) : (
                      <Button size="sm" variant="outline" onClick={() => download(t.id)} className="rounded-none text-xs gap-1" data-testid={`download-template-${t.id}`}><UploadSimple size={12} className="rotate-180" /> Download</Button>
                    )}
                    {!t.archived && <Button size="sm" variant="outline" onClick={() => archive(t.id)} className="rounded-none text-xs gap-1 text-red-700 border-red-200" data-testid={`archive-template-${t.id}`}><Archive size={12} /> Archive</Button>}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="rounded-none">
            <DialogHeader><DialogTitle>Upload Document Template</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Template Name</Label><Input value={name} onChange={e => setName(e.target.value)} className="rounded-none" data-testid="template-name-input" /></div>
              <div>
                <Label>Scope</Label>
                <Select value={scope} onValueChange={setScope}>
                  <SelectTrigger className="rounded-none" data-testid="template-scope-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="lms">LMS</SelectItem>
                    <SelectItem value="pod">POD</SelectItem>
                    <SelectItem value="both">Both</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Programme (optional)</Label>
                <Select value={programmeId || "all"} onValueChange={v => setProgrammeId(v === "all" ? "" : v)}>
                  <SelectTrigger className="rounded-none"><SelectValue placeholder="Any programme" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Any programme</SelectItem>
                    {programmes.map(p => <SelectItem key={p.id} value={p.id}>{p.title}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Description (optional)</Label><Input value={description} onChange={e => setDescription(e.target.value)} className="rounded-none" /></div>
              <div>
                <Label>Source</Label>
                <div className="flex gap-3 mt-1">
                  <label className="flex items-center gap-1 text-xs cursor-pointer">
                    <input type="radio" name="tpl-source" checked={source === "file"} onChange={() => setSource("file")} data-testid="template-source-file" /> Upload file
                  </label>
                  <label className="flex items-center gap-1 text-xs cursor-pointer">
                    <input type="radio" name="tpl-source" checked={source === "link"} onChange={() => setSource("link")} data-testid="template-source-link" /> External link
                  </label>
                </div>
              </div>
              {source === "file" ? (
                <div><Label>File</Label><Input type="file" accept=".pdf,.docx,.doc,.png,.jpg,.jpeg" onChange={e => setFile(e.target.files?.[0])} className="rounded-none" data-testid="template-file-input" /></div>
              ) : (
                <div>
                  <Label>External URL</Label>
                  <Input type="url" value={externalUrl} onChange={e => setExternalUrl(e.target.value)} placeholder="https://drive.google.com/…" className="rounded-none" data-testid="template-link-input" />
                  <p className="text-[10px] text-slate-500 mt-1">Paste a public link (Google Drive, Dropbox, etc.). The link is stored as-is — no file is uploaded.</p>
                </div>
              )}
              <Button disabled={saving} onClick={submit} className="bg-blue-800 text-white hover:bg-blue-900 rounded-none w-full" data-testid="upload-template-submit">
                {saving ? "Uploading…" : "Upload Template"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
